'use client';

import { CSSProperties, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CDPReactProvider, Config, SignInModal } from '@coinbase/cdp-react';
import { LoadingSpinner } from '@coinbase/cdp-react/components/ui/LoadingSpinner';
import { initialState } from 'apps/cdp-web-demo/app/data/config';
import { lightTheme } from 'apps/cdp-web-demo/app/data/themes';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';

import { ConfigForm } from './components/ConfigForm/ConfigForm';
import { DemoContent } from './components/DemoContent/DemoContent';
import { Tabs } from './components/Tabs/Tabs';
import { buildTheme } from './utils/buildTheme';
import { generateCodeSnippet } from './utils/generateCodeSnippet';
import { useConfig } from './ConfigContext';
import styles from './page.module.css';
import { UserProvider, useUser } from './UserContext';

const CDP_CONFIG: Config = {
  projectId: process.env.NEXT_PUBLIC_CDP_PROJECT_ID ?? '',
  basePath: process.env.NEXT_PUBLIC_CDP_BASE_PATH ?? '',
  secureIframeBasePath: process.env.NEXT_PUBLIC_CDP_SECURE_IFRAME_BASE_PATH ?? '',
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  ethereum: initialState.config.ethereum!,
  solana: initialState.config.solana,
  appName: initialState.config.appName,
  appLogoUrl: initialState.config.appLogoUrl,
  authMethods: initialState.config.authMethods,
  showCoinbaseFooter: initialState.config.showCoinbaseFooter,
  disableAnalytics: process.env.NEXT_PUBLIC_CDP_DISABLE_ANALYTICS === 'true',
};

type DemoProps = {
  style: CSSProperties;
  className?: string;
  code: string;
  configFormContainerRef: RefObject<HTMLDivElement>;
};

function Demo({ style, className = '', configFormContainerRef, code }: DemoProps) {
  const { isTablet, isDesktop } = useBreakpoints();
  const isWideLayout = isTablet || isDesktop;
  const {
    isInitialized,
    isLoggedIn,
    isModalVisible,
    setIsModalVisible,
    onSignInSuccess,
    loggedInView,
    setLoggedInView,
  } = useUser();

  const [isAnimatingDialog, setIsAnimatingDialog] = useState(false);

  const handleSendTransaction = useCallback(() => {
    if (loggedInView) {
      setLoggedInView(null);
    }
    if (!isWideLayout) {
      // scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    const btn = document.querySelector<HTMLDivElement>('[data-button-container] button');
    if (btn) {
      btn.click();
      return;
    }
    const faucetLink = document.querySelector<HTMLAnchorElement>('[data-faucet-link]');
    if (faucetLink) {
      faucetLink.focus();
      faucetLink.querySelector<HTMLSpanElement>('span')?.classList.add('animate-x-shake');
      setTimeout(() => {
        faucetLink.querySelector<HTMLSpanElement>('span')?.classList.remove('animate-x-shake');
      }, 500);
    }
  }, [loggedInView, setLoggedInView, isWideLayout]);

  const handleOpenDialog = useCallback(
    (view: typeof loggedInView) => {
      if (loggedInView === view) {
        setIsAnimatingDialog(true);
        setTimeout(() => {
          setIsAnimatingDialog(false);
        }, 500);
      } else {
        setLoggedInView(view);
      }
    },
    [loggedInView, setLoggedInView],
  );

  const handleExportWallet = useCallback(() => {
    handleOpenDialog('export-wallet');
  }, [handleOpenDialog]);

  const handleLinkProfile = useCallback(() => {
    handleOpenDialog('link-profile');
  }, [handleOpenDialog]);

  const handleAddMfa = useCallback(() => {
    handleOpenDialog('add-mfa');
  }, [handleOpenDialog]);

  return (
    <div
      className={`${styles.page} ${className} ${isLoggedIn ? styles['logged-in'] : ''}`}
      style={style}
    >
      <div
        key="config-form-container"
        ref={configFormContainerRef}
        className={styles['form-logged-in-container']}
      >
        {!isWideLayout && (
          <DemoContent
            className={`${styles['demo-content-mobile']} ${isInitialized && isLoggedIn ? styles['logged-in'] : ''}`}
          >
            <SignInModal
              open={isModalVisible}
              setIsOpen={setIsModalVisible}
              onSuccess={onSignInSuccess}
            >
              <Button
                className={`${styles['demo-content-mobile-login-button']} ${isModalVisible ? styles.hidden : ''} `}
                elevation={2}
              >
                Preview
              </Button>
            </SignInModal>
          </DemoContent>
        )}
        {(isWideLayout || isInitialized) && (
          <div className={`${styles['form-wrapper']}`}>
            <ConfigForm
              isLoggedIn={isLoggedIn}
              isInitialized={isInitialized}
              addMfa={handleAddMfa}
              exportWallet={handleExportWallet}
              linkProfile={handleLinkProfile}
              sendTransaction={handleSendTransaction}
            />
          </div>
        )}
      </div>
      {!isInitialized && !isWideLayout && (
        <div className={styles['mobile-loading-wrapper']}>
          <LoadingSpinner />
        </div>
      )}

      {isWideLayout && (
        <div
          className={`${styles['tabs-wrapper']}`}
          data-animate-dialog={isAnimatingDialog ? true : undefined}
        >
          <Tabs code={code} />
        </div>
      )}
    </div>
  );
}

export default function DemoPage() {
  const [code, setCode] = useState('');

  const configFormContainerRef = useRef<HTMLDivElement>(null);

  const { state } = useConfig();
  const { config, accentColor, borderRadius, fontFamily, themeMode } = state;

  const theme = useMemo(
    () => buildTheme({ themeMode, accentColor, borderRadius, fontFamily }),
    [themeMode, accentColor, borderRadius, fontFamily],
  );

  const iframeStyle = useMemo(() => {
    return {
      '--page-bg-tile-image': `url(/tile-${theme['colors-bg-default'] === lightTheme['colors-bg-default'] ? 'light' : 'dark'}.svg)`,
      '--page-bg-color': theme['colors-bg-alternate']
        ? `color-mix(in oklab, ${theme['colors-bg-alternate']}, ${theme['colors-bg-default']})`
        : `color-mix(in oklab, ${lightTheme['colors-bg-alternate']}, ${lightTheme['colors-bg-default']})`,
      '--dnd-flash-bg-color': `${theme['colors-bg-primary'] ?? lightTheme['colors-bg-primary']}40`,
    } as CSSProperties;
  }, [theme]);

  const props = useMemo(() => {
    // set eth config to app default if both eth and solana configs are undefined
    const ethConfig =
      !config.ethereum?.createOnLogin && !config.solana?.createOnLogin
        ? ({
            createOnLogin: 'smart',
          } as const)
        : undefined;
    return ethConfig
      ? {
          config: {
            ...config,
            ethereum: ethConfig,
          },
          theme,
        }
      : { config, theme };
  }, [config, theme]);

  useEffect(() => {
    setCode(generateCodeSnippet(props));
  }, [props]);

  const cdpConfig = useMemo(
    () => ({
      ...CDP_CONFIG,
      ...state.config,
    }),
    [state.config],
  );

  return (
    <main className={styles.wrapper}>
      <CDPReactProvider config={cdpConfig} theme={theme}>
        <UserProvider>
          <Demo code={code} style={iframeStyle} configFormContainerRef={configFormContainerRef} />
        </UserProvider>
      </CDPReactProvider>
    </main>
  );
}
